import { Component, HostBinding, OnDestroy, ViewChild } from '@angular/core';
import { CellActionType, CellTemplateType, GenericColDef, GridContext, SortByFilter } from '../../../../models/grid-context';
import { DrRequestInfo } from '../../../../models/dr-request-info';
import { AnalyticNodeMode, Endpoint, HelpIndex, MessageType, UpdateStatusType } from '../../../../models/enums';
import { RealmNode } from '../../../../models/realm-node';
import { CellClickedEvent, RowNode } from 'ag-grid-community';
import { CommonService } from '../../../../services/common/common.service';
import { NewRealmNodeEditComponent } from '../../new-realm-node-edit/new-realm-node-edit.component';
import { GenericGridComponent } from '../../../generic-grid/generic-grid.component';
import { DeleteEntityModalComponent } from '../../delete-entity-modal/delete-entity-modal.component';
import { RestService } from '../../../../services/rest/rest.service';
import { Subscription } from 'rxjs';
import { ActionType, ObjectActionListType, Permissions } from '../../../../models/permission';

@Component({
  standalone: false,
  selector: 'res-realm-nodes',
  templateUrl: './realm-nodes.component.html',
  styleUrls: ['./realm-nodes.component.scss']
})
export class RealmNodesComponent implements OnDestroy {
  @HostBinding('class') classes = 'maximized-component';
  @ViewChild(GenericGridComponent, { static: true }) genericGrid: GenericGridComponent;

  gridName = 'Realm Nodes';
  automationId = 'realm-nodes';
  isBusy = false;
  isBusyMessage = 'Loading Realm Nodes ...';
  helpIndex = HelpIndex.sys_realm_node_summary_dlg;
  newRealmNodeBtn = 'New Realm Node ...';
  realmNodesType = []
  validChanges = true;

  isDirty: boolean = false;
  hasRealmNodesAddEditPermissions: boolean;
  hasRealmNodesDeletePermissions: boolean;

  private entityChangedEvent: Subscription;

  gridContext = new GridContext({
    sortBy: 'name',
    sortAscending: true,
  });

  columnDefs = new Array<GenericColDef>(
    {
      headerName: 'Name',
      field: 'name',
      automationId: this.automationId,
      suppressMovable: true,
      sortBy: 'name',
    },
    {
      headerName: 'IP Address',
      field: 'ipAddress',
      automationId: this.automationId,
      suppressSizeToFit: true,
      lockVisible: false
    },
    {
      headerName: 'Type',
      field: 'analyticNodeMode',
      cellTemplateType: CellTemplateType.realmNodesType,
      automationId: this.automationId,
      suppressSizeToFit: true,
      minWidth: 265, maxWidth: 265, width: 265,
      lockVisible: false,
    },
    {
      headerName: 'Status',
      field: 'monitorStatusUI',
      cellTemplateType: CellTemplateType.nameIconLabel,
      automationId: this.automationId,
      suppressSizeToFit: true,
      minWidth: 100, maxWidth: 100, width: 100,
      lockVisible: false
    },
    {
      headerName: 'Temporary Storage Name',
      field: 'storageName',
      automationId: this.automationId,
      lockVisible: false,
    },
    {
      headerName: 'Temporary Storage Path',
      field: 'storageExport',
      automationId: this.automationId,
      lockVisible: false,
    },
    {
      colId: 'delete',
      automationId: this.automationId,
      cellTemplateType: CellTemplateType.cellButton,
      minWidth: 30, maxWidth: 30, width: 30,
      suppressSizeToFit: true
    }
  );

  requestInfo = new DrRequestInfo({
    endpoint: Endpoint.realmManager_listNodes,
    responseName: 'nodes',
    data: {
      sortByFilter: "NAME",
      descending: false,
      contextHandle: 'super_system_customer'
    }
  });

  constructor(private commonService: CommonService, private restService: RestService) {
    this.entityChangedEvent = this.commonService.getMessage(MessageType.entityChanged).subscribe((data) => {
      if (data === UpdateStatusType.REALM_NODE_VIEW) {
        this.genericGrid.loadData();
      }
    });
  }

  selectedTreeItemChanged(): void {
    this.hasRealmNodesAddEditPermissions = Permissions.checkUserPermission(ObjectActionListType.REALM_NODES, ActionType.EDIT);
    this.hasRealmNodesDeletePermissions = Permissions.checkUserPermission(ObjectActionListType.REALM_NODES, ActionType.DELETE);

    if (!this.hasRealmNodesDeletePermissions) {
      this.columnDefs = this.columnDefs.filter(col => col.colId !== 'delete');
    }
  }

  responseArrayCallbackRealmNodes = (realmNode: RealmNode) => new RealmNode(realmNode);

  responseCallback = (response: any) => {
    const realmNodes = response.nodes;
    this.realmNodesType = realmNodes?.length > 0 ? realmNodes.map(realmNodes => realmNodes.analyticNodeMode) : [];
  }

  cellClickedCallback = (rowNode: RowNode, action: CellActionType, event: CellClickedEvent) => {
    if (rowNode && rowNode.data instanceof RealmNode && action === CellActionType.cellButton) {
      const node = new RealmNode(rowNode.data);
      this.commonService.openModal(DeleteEntityModalComponent, { modalData: node });
    }
    if (rowNode && rowNode.data instanceof RealmNode && action === CellActionType.selectStandard) {
      rowNode.data.analyticNodeMode = AnalyticNodeMode.STANDARD;
      event.api.refreshCells();
      this.isDirty = true;
    }
    if (rowNode && rowNode.data instanceof RealmNode && action === CellActionType.selectExpress) {
      rowNode.data.analyticNodeMode = AnalyticNodeMode.EXPRESS;
      event.api.refreshCells();
      this.isDirty = true;
    }
    if (rowNode && rowNode.data instanceof RealmNode && action === CellActionType.selectOcr) {
      rowNode.data.analyticNodeMode = AnalyticNodeMode.OCR;
      event.api.refreshCells();
      this.isDirty = true;
    }
  }

  addNewRealmNode(): void {
    this.commonService.openModal(NewRealmNodeEditComponent).then(response => {
      if (response !== null && response.modalResult === true) {
        this.genericGrid.loadData();
      }
    });
  }

  isDisabled(): boolean {
    let isDisabled = true;
    if (!this.isBusy && this.isDirty && this.realmNodesTypeChanged()) {
      isDisabled = false;
    }
    return isDisabled;
  }

  createPayload(): any[] {
    let payload = [];
    this.genericGrid.initialLoadData.forEach(node => {
      const obj = {
        analyticNodeMode: node["analyticNodeMode"],
        nodeHandle: node["handle"]
      }
      payload.push(obj);
    });
    return payload;
  }

  saveChanges(): void {
    this.isBusy = true;
    this.isDirty = false;
    const payload = this.createPayload();
    const request = { updateNodesRequest: payload };
    this.restService.post(Endpoint.realmManager_updateNodes, request).then(() => {
      this.isBusy = false;
    }, (error) => {
      this.discardChanges();
      this.isBusy = false;
    });
  }

  discardChanges(): void {
    this.isDirty = false;
    this.genericGrid.loadData();
  }

  realmNodesTypeChanged(): boolean {
    const gridList = this.genericGrid.getData();
    if (gridList?.length === this.realmNodesType.length) {
      for (let i = 0; i < this.realmNodesType.length; i++) {
        if (this.realmNodesType[i] !== gridList[i]?.analyticNodeMode) {
          return true;
        }
      }
    }
    this.isDirty = false;
    return false;
  }

  ngOnDestroy(): void {
    if (this.entityChangedEvent) {
      this.entityChangedEvent.unsubscribe();
    }
    this.isDirty = false;
  }
}